import { dataService } from './dataService.js';
import { appStore } from '../store/appStore.js';
import { lastInvocationAt } from './api.js';

/**
 * Service responsible for periodic data refresh to keep the UI in sync
 * across multiple devices.
 */
class DataRefreshService {
  private refreshTimer?: number;
  private isRefreshing = false;
  private readonly REFRESH_INTERVAL = 30000; // 30 seconds
  private readonly MIN_TIME_BETWEEN_REFRESHES = 10000; // 10 seconds - skip refresh if endpoint was called recently

  start() {
    if (this.refreshTimer) {
      console.warn('DataRefreshService already started');
      return;
    }

    this.refreshTimer = window.setInterval(() => {
      this.refreshData();
    }, this.REFRESH_INTERVAL);

    console.log('DataRefreshService started with 30-second interval');
  }

  stop() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
      console.log('DataRefreshService stopped');
    }
  }

  private async refreshData() {
    if (this.isRefreshing) {
      console.log('DataRefreshService: Skipping refresh (already in progress)');
      return;
    }

    const state = appStore.getState();

    if (!state.isAuthenticated) {
      return;
    }

    if (state.isLoading) {
      console.log('DataRefreshService: Skipping refresh (loading in progress)');
      return;
    }

    this.isRefreshing = true;

    try {
      const { currentCycle, selectedFacility, expandedRoutePrefix, expandedRoute } = state;
      const refreshPromises: Promise<void>[] = [];
      const now = Date.now();

      // Always refresh route prefixes and pack lines if we have a selected cycle and facility
      if (currentCycle && selectedFacility) {
        // Only refresh if last invocation was not too recent
        if (now - lastInvocationAt.routePrefixes >= this.MIN_TIME_BETWEEN_REFRESHES) {
          refreshPromises.push(
            dataService.loadRoutePrefixData(currentCycle.id, selectedFacility.id, false)
          );
        } else {
          console.log('DataRefreshService: Skipping routePrefixes refresh (invoked recently)');
        }

        if (now - lastInvocationAt.packLines >= this.MIN_TIME_BETWEEN_REFRESHES) {
          refreshPromises.push(
            dataService.loadPackLines(currentCycle.id, selectedFacility.id, false)
          );
        } else {
          console.log('DataRefreshService: Skipping packLines refresh (invoked recently)');
        }

        // Refresh routes if a route prefix is expanded
        if (expandedRoutePrefix && now - lastInvocationAt.routes >= this.MIN_TIME_BETWEEN_REFRESHES) {
          refreshPromises.push(
            dataService.loadRoutesByRoutePrefix(
              expandedRoutePrefix.cycleId,
              expandedRoutePrefix.routePrefixId,
              selectedFacility.id,
              false
            ).then(() => {})
          );
        } else if (expandedRoutePrefix) {
          console.log('DataRefreshService: Skipping routes refresh (invoked recently)');
        }
      }

      // Refresh deliveries if a route is expanded
      if (expandedRoute && now - lastInvocationAt.deliveries >= this.MIN_TIME_BETWEEN_REFRESHES) {
        refreshPromises.push(
          dataService.loadDeliveriesByRouteId(expandedRoute.routeId, false).then(() => {})
        );
      } else if (expandedRoute) {
        console.log('DataRefreshService: Skipping deliveries refresh (invoked recently)');
      }

      // Execute all refreshes in parallel without showing loading overlay
      if (refreshPromises.length > 0) {
        await Promise.all(refreshPromises);
        console.log('DataRefreshService: Refreshed all data');
      }
    } catch (error) {
      console.error('DataRefreshService: Error during refresh:', error);
    } finally {
      this.isRefreshing = false;
    }
  }
}

export const dataRefreshService = new DataRefreshService();
